import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BlenderBladeLogEntry,
  CalibrationRecord,
  CalibrationTemplate,
  CalibrationTemplateItem,
  CompleteBlenderBladeLogEntryRequest,
  CreateBlenderBladeLogEntryRequest,
  CreateCalibrationRecordRequest,
  InstrumentCalibrationItem,
  InstrumentLifecycleEvent,
  LabEquipmentAttachment,
  LabEquipmentInstrument,
  LabEquipmentInstrumentQuery,
  LabEquipmentInstrumentRequest,
  LabEquipmentInstrumentType,
  LabEquipmentLocation,
  MaintenanceRecord,
  PagedResult,
  UpdateCalibrationRecordRequest,
  UpdateLabEquipmentInstrumentRequest,
} from './lab-equipment.models';

@Injectable({ providedIn: 'root' })
export class LabEquipmentService {
  private readonly baseUrl = `${environment.apiUrl}/lab-equipment`;
  private readonly http = inject(HttpClient);

  getLocations(): Observable<LabEquipmentLocation[]> {
    return this.http.get<LabEquipmentLocation[]>(`${this.baseUrl}/locations`);
  }

  getInstrumentTypes(): Observable<LabEquipmentInstrumentType[]> {
    return this.http.get<LabEquipmentInstrumentType[]>(`${this.baseUrl}/instrument-types`);
  }

  createInstrumentType(name: string): Observable<LabEquipmentInstrumentType> {
    return this.http.post<LabEquipmentInstrumentType>(`${this.baseUrl}/instrument-types`, { name, isActive: true });
  }

  updateInstrumentType(type: LabEquipmentInstrumentType, name: string, isActive: boolean): Observable<LabEquipmentInstrumentType> {
    return this.http.put<LabEquipmentInstrumentType>(`${this.baseUrl}/instrument-types/${type.id}`, { name, isActive });
  }

  updateTypeCalibrations(typeId: number, itemIds: number[]): Observable<LabEquipmentInstrumentType> {
    return this.http.put<LabEquipmentInstrumentType>(`${this.baseUrl}/instrument-types/${typeId}/calibrations`, {
      calibrationTemplateItemIds: itemIds,
    });
  }

  getInstruments(query: LabEquipmentInstrumentQuery): Observable<PagedResult<LabEquipmentInstrument>> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('pageSize', query.pageSize)
      .set('sort', query.sort)
      .set('descending', query.descending)
      .set('status', query.status ?? 'Active');

    if (query.search) params = params.set('search', query.search);
    if (query.locationId) params = params.set('locationId', query.locationId);
    if (query.equipmentNumber) params = params.set('equipmentNumber', query.equipmentNumber);
    if (query.name) params = params.set('name', query.name);
    if (query.manufacturer) params = params.set('manufacturer', query.manufacturer);
    if (query.modelNumber) params = params.set('modelNumber', query.modelNumber);
    if (query.serialNumber) params = params.set('serialNumber', query.serialNumber);
    if (query.assetNumber) params = params.set('assetNumber', query.assetNumber);
    if (query.dueStatus) params = params.set('dueStatus', query.dueStatus);
    if (query.vendorDueStatus) params = params.set('vendorDueStatus', query.vendorDueStatus);
    if (query.labDueStatus) params = params.set('labDueStatus', query.labDueStatus);
    for (const locationId of query.locationIds ?? []) params = params.append('locationIds', locationId);
    return this.http.get<PagedResult<LabEquipmentInstrument>>(`${this.baseUrl}/instruments`, { params });
  }

  createInstrument(request: LabEquipmentInstrumentRequest): Observable<LabEquipmentInstrument> {
    return this.http.post<LabEquipmentInstrument>(`${this.baseUrl}/instruments`, request);
  }

  updateInstrument(id: number, request: UpdateLabEquipmentInstrumentRequest): Observable<LabEquipmentInstrument> {
    return this.http.put<LabEquipmentInstrument>(`${this.baseUrl}/instruments/${id}`, request);
  }

  removeInstrument(id: number, removalDate: string, removalReason: string): Observable<LabEquipmentInstrument> {
    return this.http.post<LabEquipmentInstrument>(`${this.baseUrl}/instruments/${id}/remove`, {
      removalDate, removalReason,
    });
  }

  permanentlyDeleteInstrument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/instruments/${id}`);
  }

  restoreInstrument(id: number, restoreDate: string, restoreReason: string): Observable<LabEquipmentInstrument> {
    return this.http.post<LabEquipmentInstrument>(`${this.baseUrl}/instruments/${id}/restore`, {
      restoreDate, restoreReason,
    });
  }

  updateInstrumentRemoval(id: number, removalDate: string, removalReason: string): Observable<LabEquipmentInstrument> {
    return this.http.put<LabEquipmentInstrument>(`${this.baseUrl}/instruments/${id}/removal`, {
      removalDate, removalReason,
    });
  }

  getInstrumentLifecycleEvents(id: number): Observable<InstrumentLifecycleEvent[]> {
    return this.http.get<InstrumentLifecycleEvent[]>(`${this.baseUrl}/instruments/${id}/lifecycle-events`);
  }

  getInstrument(id: number): Observable<LabEquipmentInstrument> {
    return this.http.get<LabEquipmentInstrument>(`${this.baseUrl}/instruments/${id}`);
  }

  getBlenderBladeLog(instrumentId: number): Observable<BlenderBladeLogEntry[]> {
    return this.http.get<BlenderBladeLogEntry[]>(`${this.baseUrl}/instruments/${instrumentId}/blender-blade-log`);
  }

  createBlenderBladeLogEntry(instrumentId: number, request: CreateBlenderBladeLogEntryRequest): Observable<BlenderBladeLogEntry> {
    return this.http.post<BlenderBladeLogEntry>(`${this.baseUrl}/instruments/${instrumentId}/blender-blade-log`, request);
  }

  completeBlenderBladeLogEntry(instrumentId: number, entryId: number, request: CompleteBlenderBladeLogEntryRequest): Observable<BlenderBladeLogEntry> {
    return this.http.put<BlenderBladeLogEntry>(`${this.baseUrl}/instruments/${instrumentId}/blender-blade-log/${entryId}/complete`, request);
  }

  getCalibrationTemplates(): Observable<CalibrationTemplate[]> {
    return this.http.get<CalibrationTemplate[]>(`${this.baseUrl}/calibration-templates`);
  }

  getCalibrationTemplateItems(): Observable<CalibrationTemplateItem[]> {
    return this.http.get<CalibrationTemplateItem[]>(`${this.baseUrl}/calibration-template-items`);
  }

  getInstrumentCalibrationItems(instrumentId: number): Observable<InstrumentCalibrationItem[]> {
    return this.http.get<InstrumentCalibrationItem[]>(`${this.baseUrl}/instruments/${instrumentId}/calibration-items`);
  }

  assignCalibrationItem(instrumentId: number, calibrationTemplateItemId: number, initialDueDate: string): Observable<InstrumentCalibrationItem> {
    return this.http.post<InstrumentCalibrationItem>(`${this.baseUrl}/instruments/${instrumentId}/calibration-items`, {
      calibrationTemplateItemId, initialDueDate,
    });
  }

  updateCalibrationAssignment(instrumentId: number, assignmentId: number, isActive: boolean, initialDueDate: string | null = null): Observable<InstrumentCalibrationItem> {
    return this.http.put<InstrumentCalibrationItem>(
      `${this.baseUrl}/instruments/${instrumentId}/calibration-items/${assignmentId}`,
      { isActive, initialDueDate },
    );
  }

  updateNextDueOverride(instrumentId: number, assignment: InstrumentCalibrationItem, nextDueDateOverride: string | null, reason: string): Observable<InstrumentCalibrationItem> {
    return this.http.put<InstrumentCalibrationItem>(
      `${this.baseUrl}/instruments/${instrumentId}/calibration-items/${assignment.id}`,
      { isActive: assignment.isActive, initialDueDate: null, updateNextDueDateOverride: true, nextDueDateOverride, nextDueDateOverrideReason: reason },
    );
  }

  deleteCalibrationAssignment(instrumentId: number, assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/instruments/${instrumentId}/calibration-items/${assignmentId}`);
  }

  getCalibrationRecords(instrumentId: number, year?: number, month?: number, quarter?: number, week?: number): Observable<PagedResult<CalibrationRecord>> {
    let params = new HttpParams().set('instrumentId', instrumentId).set('page', 1).set('pageSize', 100);
    if (year) params = params.set('year', year);
    if (month) params = params.set('month', month);
    if (quarter) params = params.set('quarter', quarter);
    if (week) params = params.set('week', week);
    return this.http.get<PagedResult<CalibrationRecord>>(`${this.baseUrl}/calibration-records`, { params });
  }

  createCalibrationRecord(instrumentId: number, request: CreateCalibrationRecordRequest): Observable<CalibrationRecord> {
    return this.http.post<CalibrationRecord>(`${this.baseUrl}/instruments/${instrumentId}/calibration-records`, request);
  }

  updateCalibrationRecord(recordId: number, request: UpdateCalibrationRecordRequest): Observable<CalibrationRecord> {
    return this.http.put<CalibrationRecord>(`${this.baseUrl}/calibration-records/${recordId}`, request);
  }

  addCalibrationAddendum(recordId: number, text: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/calibration-records/${recordId}/addenda`, { text });
  }

  getMaintenanceRecords(instrumentId: number): Observable<PagedResult<MaintenanceRecord>> {
    return this.http.get<PagedResult<MaintenanceRecord>>(`${this.baseUrl}/instruments/${instrumentId}/maintenance`, {
      params: new HttpParams().set('page', 1).set('pageSize', 100),
    });
  }

  createMaintenanceRecord(instrumentId: number, type: 'Maintenance' | 'Repair', performedDate: string, notes: string): Observable<MaintenanceRecord> {
    return this.http.post<MaintenanceRecord>(`${this.baseUrl}/instruments/${instrumentId}/maintenance`, {
      type, performedDate, notes,
    });
  }

  getAttachments(instrumentId: number): Observable<PagedResult<LabEquipmentAttachment>> {
    return this.http.get<PagedResult<LabEquipmentAttachment>>(`${this.baseUrl}/instruments/${instrumentId}/attachments`, {
      params: new HttpParams().set('page', 1).set('pageSize', 100),
    });
  }

  uploadAttachment(instrumentId: number, file: File, maintenanceRecordId?: number, documentDate?: string): Observable<HttpEvent<LabEquipmentAttachment>> {
    const form = new FormData();
    form.append('file', file);
    if (maintenanceRecordId) form.append('maintenanceRecordId', maintenanceRecordId.toString());
    if (documentDate) form.append('documentDate', documentDate);
    return this.http.post<LabEquipmentAttachment>(`${this.baseUrl}/instruments/${instrumentId}/attachments`, form, {
      observe: 'events',
      reportProgress: true,
    });
  }

  downloadAttachment(attachment: LabEquipmentAttachment): void {
    this.http.get(`${this.baseUrl}/attachments/${attachment.id}/download`, { responseType: 'blob' }).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.originalFileName;
      anchor.click();
      URL.revokeObjectURL(url);
    });
  }

  deleteAttachment(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/attachments/${id}`);
  }
}
